import { useMemo, useState } from "react";
import { ProductMedia } from "../../../../../graphql/operations";
import useWindowSize from "../../../../../utils/useWindowSize";
import { CloudinaryImage, CloudinaryVideo } from "../../../../Atoms/Cloudinary";
import ImageGallery from "../../../../Atoms/ImageGallery";
import styles from "./ProductMediaGallery.module.scss";

type Props = {
  media: ProductMedia[];
  productId: string;
  productName: string;
  shouldGalleryOpen: boolean;
  setGalleryOpen: (open: boolean) => void;
};

export default function ProductMediaGalleryNew({
  media,
  productId,
  productName,
  shouldGalleryOpen,
  setGalleryOpen,
}: Props) {
  const { width } = useWindowSize();
  const [isSeeking, setSeeking] = useState(false);
  const [isMouseOverVideo, setMouseOverVideo] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [initialImage, setInitialImage] = useState<string>();

  const [images, videos] = useMemo(
    () =>
      media.reduce(
        (acc, item) => {
          acc[Number(item.type === "video")].push(item);
          return acc;
        },
        [[], []] as [ProductMedia[], ProductMedia[]],
      ),
    [media],
  );

  const shouldControlsShow = () => {
    if (isVideoPlaying && !isMouseOverVideo) return false;
    return true;
  };

  return (
    <div className={styles.ProductMediaGallery}>
      <div className="AppLayout">
        <div className={styles.MediaGrid}>
          {media.slice(0, 5).map((item, index) => (
            <div
              className={`${styles.MediaItem} ${
                index === 0 ? styles.FeaturedMedia : ""
              }`}
              key={item.url}
            >
              {item.type === "video" ? (
                <div className={styles.VideoContainer}>
                  <CloudinaryVideo
                    src={item}
                    muted
                    playsInline
                    autoPlay={index === 0}
                    onPlaying={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                    onEnded={() => setIsVideoPlaying(false)}
                    controls={shouldControlsShow()}
                    onMouseOver={() => setMouseOverVideo(true)}
                    onMouseLeave={() => setMouseOverVideo(false)}
                    onSeeking={() => setSeeking(true)}
                    onSeeked={() => setSeeking(false)}
                    transformations={["h_400"]}
                  />
                </div>
              ) : (
                <div
                  onClick={() => {
                    setInitialImage(item.url);
                    if (typeof window.gtag === "function") {
                      window.gtag("event", "product_photo_click", {
                        product_id: productId,
                        index,
                        url: item.url,
                      });
                    }
                  }}
                  className={styles.SliderImageHolder}
                >
                  <CloudinaryImage
                    src={item.url}
                    alt={`${productName}${
                      item.category ? ` ${item.category}` : ""
                    }`}
                    transformations={["h_500"]}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <section
        className={`AppLayout ${styles.GalleryActions}`}
        style={
          isVideoPlaying && width < 839 && !isSeeking
            ? { display: "none" }
            : { display: "block" }
        }
      >
        <ImageGallery
          showViewAllButton={media.length > 5}
          buttonClassName={styles.ViewAllPhotosButton}
          setGalleryOpen={setGalleryOpen}
          setInitialImage={setInitialImage}
          initialImage={initialImage}
          openGallery={shouldGalleryOpen}
          photos={images}
          videos={videos}
          isProductPage={true}
          onClickPhoto={(index, { url }) =>
            window &&
            typeof window.gtag === "function" &&
            window.gtag("event", "product_photo_click", {
              product_id: productId,
              index,
              url,
            })
          }
        />
      </section>
    </div>
  );
}
